import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Search, X, Filter, Calendar, DollarSign, Building2, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { SearchFilters, DocumentStatus, SearchFilterControlsConfig } from '../types';
import { useDocumentStore } from '../store/documentStore';
import { apiClient, apiEndpoints } from '../services/apiClient';

interface SearchBarProps {
  onSearch: (filters: SearchFilters) => void;
  initialFilters?: SearchFilters;
}

const DEFAULT_SEARCH_FILTER_CONTROLS: SearchFilterControlsConfig = {
  excludedFieldNames: ['-', '–', '—', '.', ',', '_'],
  minFieldNameLength: 2,
  hideNumericOnlyKeys: true,
  trimAndExcludeWhitespaceOnly: true,
  adminFieldNames: [],
};

function applySearchFilterControls(
  fieldName: string,
  config: SearchFilterControlsConfig
): boolean {
  let key = fieldName;
  if (config.trimAndExcludeWhitespaceOnly) {
    key = key.trim();
    if (key === '') return false;
  }
  const blocklist = new Set(config.excludedFieldNames.map(s => s.trim()));
  if (blocklist.has(key)) return false;
  if (key.length < config.minFieldNameLength) return false;
  if (config.hideNumericOnlyKeys && /^\d+$/.test(key)) return false;
  return true;
}

const SearchBar: React.FC<SearchBarProps> = ({ onSearch, initialFilters = {} }) => {
  const { documents, loading } = useDocumentStore();
  const location = useLocation();
  const isSearchPage = location.pathname === '/search';
  const isSearching = loading;
  
  const [query, setQuery] = useState(initialFilters.query || '');
  const [dateFrom, setDateFrom] = useState(initialFilters.dateFrom || '');
  const [dateTo, setDateTo] = useState(initialFilters.dateTo || '');
  const [vendor, setVendor] = useState(initialFilters.vendor || '');
  const [amountMin, setAmountMin] = useState(initialFilters.amountMin?.toString() || '');
  const [amountMax, setAmountMax] = useState(initialFilters.amountMax?.toString() || '');
  const [datePreset, setDatePreset] = useState<'all_time' | 'today' | 'this_week' | 'week' | 'month' | 'year' | 'custom'>(
    initialFilters.datePreset || 'all_time'
  );
  const [status, setStatus] = useState<DocumentStatus[]>(initialFilters.status || (isSearchPage ? [] : ['active', 'pending']));
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  // Search filter controls (admin-configured rules for Filter by Field dropdown)
  const [searchFilterControls, setSearchFilterControls] = useState<SearchFilterControlsConfig | null>(null);

  // Field-value filter state
  const [selectedField, setSelectedField] = useState<string>('');
  const [fieldValueSearch, setFieldValueSearch] = useState<string>('');
  const [fieldFilters, setFieldFilters] = useState<Record<string, string>>(initialFilters.fieldFilters || {});
  const [showFieldValueFilter, setShowFieldValueFilter] = useState(false);
  const [showFieldValueDropdown, setShowFieldValueDropdown] = useState(false);
  const [fieldSearchTerm, setFieldSearchTerm] = useState<string>('');

  // Rehydrate local UI state when parent-provided filters change (e.g., back/forward, shared URL).
  useEffect(() => {
    setQuery(initialFilters.query || '');
    setDateFrom(initialFilters.dateFrom || '');
    setDateTo(initialFilters.dateTo || '');
    setVendor(initialFilters.vendor || '');
    setAmountMin(initialFilters.amountMin?.toString() || '');
    setAmountMax(initialFilters.amountMax?.toString() || '');
    setDatePreset((initialFilters.datePreset as any) || 'all_time');
    setStatus(initialFilters.status || (isSearchPage ? [] : ['active', 'pending']));
    setFieldFilters(initialFilters.fieldFilters || {});

    // Reset field picker UI; active filters still display via badges.
    setSelectedField('');
    setFieldValueSearch('');
    setFieldSearchTerm('');
    setShowFieldValueFilter(false);
    setShowFieldValueDropdown(false);
  }, [initialFilters, isSearchPage]);

  // Refetch when on Documents/Deleted so saved changes from Search Filter Controls take effect
  const isDocumentsPage = location.pathname === '/' || location.pathname === '/deleted';
  useEffect(() => {
    if (!isDocumentsPage) return;
    const load = async () => {
      const res = await apiClient.get<SearchFilterControlsConfig>(
        apiEndpoints.searchFilterControls.getConfig()
      );
      if (res.data) setSearchFilterControls(res.data);
      else setSearchFilterControls(DEFAULT_SEARCH_FILTER_CONTROLS);
    };
    load();
  }, [isDocumentsPage]);

  // Extract all unique field names from admin config + all documents (both storedFields and customFields)
  const allFields = useMemo(() => {
    const fieldSet = new Set<string>();
    const adminFieldNames = searchFilterControls?.adminFieldNames || [];

    // Seed with admin-maintained field names so dropdown remains useful even when result set is empty
    adminFieldNames.forEach(name => {
      if (name && name.trim()) {
        fieldSet.add(name);
      }
    });

    documents.forEach(doc => {
      // Add all stored field names (check if storedFields exists and is an object)
      if (doc.storedFields && typeof doc.storedFields === 'object') {
        Object.keys(doc.storedFields).forEach(key => {
          if (key && doc.storedFields[key] !== null && doc.storedFields[key] !== undefined && doc.storedFields[key] !== '') {
            fieldSet.add(key);
          }
        });
      }
      // Add all custom field names
      if (doc.customFields && Array.isArray(doc.customFields)) {
        doc.customFields.forEach(cf => {
          if (cf && cf.name && cf.value) {
            fieldSet.add(cf.name);
          }
        });
      }
    });
    return Array.from(fieldSet).sort();
  }, [documents, searchFilterControls?.adminFieldNames]);

  const filterConfig = searchFilterControls ?? DEFAULT_SEARCH_FILTER_CONTROLS;
  const cleanedFields = useMemo(() => {
    return allFields.filter(name => applySearchFilterControls(name, filterConfig));
  }, [allFields, filterConfig]);

  // Extract unique values for a selected field
  const fieldValues = useMemo(() => {
    if (!selectedField) return [];
    
    const valueSet = new Set<string>();
    documents.forEach(doc => {
      // Check stored fields (ensure storedFields exists and is an object)
      if (doc.storedFields && typeof doc.storedFields === 'object' && doc.storedFields[selectedField]) {
        const value = String(doc.storedFields[selectedField]);
        if (value && value.trim()) {
          valueSet.add(value);
        }
      }
      // Check custom fields
      if (doc.customFields && Array.isArray(doc.customFields)) {
        doc.customFields.forEach(cf => {
          if (cf && cf.name === selectedField && cf.value) {
            const value = String(cf.value);
            if (value && value.trim()) {
              valueSet.add(value);
            }
          }
        });
      }
    });
    
    const values = Array.from(valueSet).sort();
    
    // Filter by search term if provided
    if (fieldValueSearch.trim()) {
      const searchLower = fieldValueSearch.toLowerCase();
      return values.filter(v => v.toLowerCase().includes(searchLower));
    }
    
    return values;
  }, [selectedField, documents, fieldValueSearch]);

  // Filter fields by search term (within cleaned list)
  const filteredFields = useMemo(() => {
    if (!fieldSearchTerm.trim()) return cleanedFields;
    const searchLower = fieldSearchTerm.toLowerCase();
    return cleanedFields.filter(field => field.toLowerCase().includes(searchLower));
  }, [cleanedFields, fieldSearchTerm]);

  // Extract unique vendors from documents (keep for backward compatibility)
  // const uniqueVendors = React.useMemo(() => {
  //   const vendors = new Set<string>();
  //   documents.forEach(doc => {
  //     const vendor = doc.storedFields['Vendor Name'] || doc.storedFields['Vendor'];
  //     if (vendor && vendor !== 'Unknown') {
  //       vendors.add(vendor);
  //     }
  //   });
  //   return Array.from(vendors).sort();
  // }, [documents]); // Reserved for future use

  // Apply rolling date presets (not custom / not all time)
  useEffect(() => {
    if (datePreset === 'all_time') {
      setDateFrom('');
      setDateTo('');
      return;
    }
    if (datePreset === 'custom') {
      return;
    }
    const now = new Date();
    let fromDate: Date;

    switch (datePreset) {
      case 'today':
        fromDate = new Date(now);
        break;
      case 'this_week':
        fromDate = new Date(now);
        // Week starts on Sunday to match JS Date day indexing and existing local behavior.
        fromDate.setDate(now.getDate() - now.getDay());
        break;
      case 'week':
        fromDate = new Date(now);
        fromDate.setDate(now.getDate() - 7);
        break;
      case 'month':
        fromDate = new Date(now);
        fromDate.setMonth(now.getMonth() - 1);
        break;
      case 'year':
        fromDate = new Date(now);
        fromDate.setFullYear(now.getFullYear() - 1);
        break;
      default:
        return;
    }

    setDateFrom(fromDate.toISOString().split('T')[0]);
    setDateTo(now.toISOString().split('T')[0]);
  }, [datePreset]);

  const handleSearch = useCallback(() => {
    const filters: SearchFilters = {};
    if (query.trim()) filters.query = query.trim().substring(0, 50);
    if (datePreset === 'all_time') {
      filters.datePreset = 'all_time';
    } else {
      if (dateFrom) filters.dateFrom = dateFrom;
      if (dateTo) filters.dateTo = dateTo;
      if (datePreset !== 'custom') {
        filters.datePreset = datePreset;
      } else {
        filters.datePreset = 'custom';
      }
    }
    if (vendor.trim()) filters.vendor = vendor.trim();
    if (amountMin) filters.amountMin = parseFloat(amountMin);
    if (amountMax) filters.amountMax = parseFloat(amountMax);
    if (status.length > 0) filters.status = status;
    if (Object.keys(fieldFilters).length > 0) filters.fieldFilters = fieldFilters;
    onSearch(filters);
  }, [query, dateFrom, dateTo, vendor, amountMin, amountMax, datePreset, status, fieldFilters, onSearch]);

  const handleClear = () => {
    setQuery('');
    setDateFrom('');
    setDateTo('');
    setVendor('');
    setAmountMin('');
    setAmountMax('');
    setDatePreset('all_time');
    setStatus(isSearchPage ? [] : ['active', 'pending']);
    setSelectedField('');
    setFieldValueSearch('');
    setFieldSearchTerm('');
    setFieldFilters({});
    setShowFieldValueFilter(false);
    setShowFieldValueDropdown(false);
    onSearch({});
  };

  const toggleStatus = (s: DocumentStatus) => {
    setStatus(prev =>
      prev.includes(s) ? prev.filter(item => item !== s) : [...prev, s]
    );
  };

  const handleDatePresetChange = (preset: 'all_time' | 'today' | 'this_week' | 'week' | 'month' | 'year' | 'custom') => {
    setDatePreset(preset);
    if (preset === 'custom') {
      setDateFrom('');
      setDateTo('');
    }
  };

  const handleFieldSelect = (fieldName: string) => {
    setSelectedField(fieldName);
    setFieldValueSearch('');
    setFieldSearchTerm('');
    setShowFieldValueFilter(false);
    setShowFieldValueDropdown(true);
    // If field already has a filter value, set it in search
    if (fieldFilters[fieldName]) {
      setFieldValueSearch(fieldFilters[fieldName]);
    }
  };

  const handleFieldValueSelect = (value: string) => {
    if (selectedField) {
      setFieldFilters({
        ...fieldFilters,
        [selectedField]: value,
      });
      setFieldValueSearch(value);
      setShowFieldValueDropdown(false);
      // Keep field selected so user can change the value or add another filter
      // User can manually clear by clicking X on the filter badge
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.field-value-filter-container')) {
        setShowFieldValueFilter(false);
        setShowFieldValueDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const removeFieldFilter = (fieldName: string) => {
    const newFilters = { ...fieldFilters };
    delete newFilters[fieldName];
    setFieldFilters(newFilters);
    if (selectedField === fieldName) {
      setSelectedField('');
      setFieldValueSearch('');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-4 mb-6">
      <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:items-stretch sm:gap-4">
        <div className="relative min-w-0 flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value.substring(0, 50))}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !isSearching) handleSearch();
            }}
            placeholder="Search documents (max 50 characters)..."
            maxLength={50}
            aria-label="Search documents"
            aria-describedby="search-help"
            aria-busy={isSearching}
            disabled={isSearching}
            className="w-full pl-10 pr-4 py-2.5 min-h-[44px] border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:outline-none transition-all duration-200 bg-white hover:border-gray-300 disabled:cursor-wait disabled:bg-gray-50 disabled:opacity-80"
          />
          <span id="search-help" className="sr-only">Press Enter to search or use Ctrl+K to focus this field</span>
          {query && !isSearching && (
            <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-gray-500">
              {query.length}/50
            </span>
          )}
        </div>
        <div className="flex gap-2 shrink-0 sm:items-stretch">
          <button
            type="button"
            onClick={handleSearch}
            disabled={isSearching}
            aria-label={isSearching ? 'Searching documents' : 'Search documents'}
            aria-busy={isSearching}
            className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-6 py-2.5 min-h-[44px] bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg hover:from-primary-700 hover:to-primary-800 transition-all duration-200 shadow-md hover:shadow-lg font-medium disabled:cursor-wait disabled:opacity-80 disabled:hover:from-primary-600 disabled:hover:to-primary-700 disabled:hover:shadow-md"
          >
            {isSearching ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin shrink-0" aria-hidden />
                Searching...
              </>
            ) : (
              'Search'
            )}
          </button>
          <button
            type="button"
            onClick={handleClear}
            disabled={isSearching}
            aria-label="Clear search"
            className="flex items-center justify-center px-4 py-2.5 min-h-[44px] min-w-[44px] bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all duration-200 disabled:cursor-wait disabled:opacity-60"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Server-side filters */}
      <div className="mb-4">
        <p className="text-xs font-semibold text-gray-700 mb-1">Server-side filters</p>
        <p className="text-xs text-gray-500 mb-2">
          These filters change which documents are loaded from the server.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Field-Value Filter */}
          <div className="field-value-filter-container">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              <Building2 className="w-3 h-3 inline mr-1" />
              Filter by Field
            </label>
            <div className="relative">
              <button
                onClick={() => {
                  setShowFieldValueFilter(!showFieldValueFilter);
                  setFieldSearchTerm('');
                }}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-left flex items-center justify-between"
              >
                <span className="truncate">
                  {selectedField || 'Select Field...'}
                </span>
                {showFieldValueFilter ? (
                  <ChevronUp className="w-4 h-4 text-gray-400" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                )}
              </button>
              
              {showFieldValueFilter && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
                  <div className="p-2 border-b">
                    <input
                      type="text"
                      placeholder="Search fields..."
                      value={fieldSearchTerm}
                      onChange={(e) => setFieldSearchTerm(e.target.value)}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                    />
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {filteredFields.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-gray-500">No fields found</div>
                    ) : (
                      filteredFields.map(field => (
                        <button
                          key={field}
                          onClick={() => handleFieldSelect(field)}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 ${
                            selectedField === field ? 'bg-blue-50 text-blue-600' : ''
                          }`}
                        >
                          {field}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            
            {/* Show selected field value filter */}
            {selectedField && (
              <div className="mt-2 relative field-value-filter-container">
                <div className="flex space-x-1">
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      placeholder={`Search ${selectedField} values...`}
                      value={fieldValueSearch}
                      onChange={(e) => {
                        setFieldValueSearch(e.target.value);
                        setShowFieldValueDropdown(true);
                      }}
                      onFocus={() => setShowFieldValueDropdown(true)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && fieldValueSearch.trim()) {
                          handleFieldValueSelect(fieldValueSearch.trim());
                        }
                      }}
                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                    />
                    {showFieldValueDropdown && fieldValues.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {fieldValues.slice(0, 50).map(value => (
                          <button
                            key={value}
                            onClick={() => handleFieldValueSelect(value)}
                            className="w-full text-left px-3 py-2 text-xs hover:bg-gray-100"
                          >
                            {value}
                          </button>
                        ))}
                        {fieldValues.length > 50 && (
                          <div className="px-3 py-2 text-xs text-gray-500 border-t">
                            Showing first 50 of {fieldValues.length} values
                          </div>
                        )}
                      </div>
                    )}
                    {showFieldValueDropdown && fieldValueSearch && fieldValues.length === 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                        <div className="px-3 py-2 text-xs text-gray-500">
                          No matching values. Press Enter to use "{fieldValueSearch}"
                        </div>
                      </div>
                    )}
                  </div>
                  {fieldValueSearch.trim() && 
                  fieldValueSearch.trim() !== fieldFilters[selectedField] && (
                    <button
                      onClick={() => handleFieldValueSelect(fieldValueSearch.trim())}
                      className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                      title="Apply filter"
                    >
                      Apply
                    </button>
                  )}
                </div>
              </div>
            )}
            
            {/* Show active field filters */}
            {Object.keys(fieldFilters).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {Object.entries(fieldFilters).map(([field, value]) => (
                  <span
                    key={field}
                    className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded"
                  >
                    {field}: {value}
                    <button
                      onClick={() => removeFieldFilter(field)}
                      className="ml-1 text-blue-600 hover:text-blue-800"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Upload date (server) + amount (current page when using API) */}
      <div className="mb-4">
        <p className="text-xs font-semibold text-gray-700 mb-1">Upload date &amp; amount</p>
        <p className="text-xs text-gray-500 mb-2">
          <span className="font-medium text-gray-600">Upload date</span> uses each document&apos;s{' '}
          <span className="font-medium text-gray-600">upload timestamp</span> and is applied on the server when you use the hosted app.
          Amount range only narrows the documents already loaded on the current page in that mode.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {/* Upload date preset */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              <Calendar className="w-3 h-3 inline mr-1" />
              Upload period
            </label>
            <select
              value={datePreset}
              onChange={(e) => handleDatePresetChange(e.target.value as any)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all_time">All time</option>
              <option value="today">Today</option>
              <option value="this_week">This Week</option>
              <option value="week">Last Week</option>
              <option value="month">Last Month</option>
              <option value="year">Last Year</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          {/* Amount Range */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              <DollarSign className="w-3 h-3 inline mr-1" />
              Amount Range
            </label>
            <div className="flex space-x-1">
              <input
                type="number"
                value={amountMin}
                onChange={(e) => setAmountMin(e.target.value)}
                placeholder="Min"
                className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="number"
                value={amountMax}
                onChange={(e) => setAmountMax(e.target.value)}
                placeholder="Max"
                className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
      </div>

      <button
        onClick={() => setShowAdvanced(!showAdvanced)}
        className="text-sm text-blue-600 hover:text-blue-700 flex items-center"
      >
        <Filter className="w-4 h-4 mr-1" />
        {showAdvanced ? 'Hide' : 'Show'} Advanced Filters
      </button>

      {showAdvanced && (
        <div className="mt-4 pt-4 border-t space-y-4">
          {/* Advanced client-side filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Upload date from</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setDatePreset('custom');
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Upload date to</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setDatePreset('custom');
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <p className="text-xs text-gray-500">
            Calendar dates are interpreted as UTC days (inclusive), matching the stored upload time.
          </p>
          
          {/* Advanced server-side filters */}
          {isSearchPage && (
            <div className="pt-2 border-t">
              <label className="block text-sm font-medium text-gray-700 mb-2">Status (server-side filter)</label>
              <div className="flex flex-wrap gap-3">
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    className="form-checkbox rounded text-blue-600"
                    checked={status.includes('active')}
                    onChange={() => toggleStatus('active')}
                  />
                  <span className="ml-2 text-gray-700">Active</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    className="form-checkbox rounded text-yellow-600"
                    checked={status.includes('pending')}
                    onChange={() => toggleStatus('pending')}
                  />
                  <span className="ml-2 text-gray-700">Pending</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    className="form-checkbox rounded text-red-600"
                    checked={status.includes('deleted')}
                    onChange={() => toggleStatus('deleted')}
                  />
                  <span className="ml-2 text-gray-700">Deleted</span>
                </label>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Changing status reloads documents from the server.
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {status.length === 0 ? 'All statuses selected' : `${status.length} status(es) selected`}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
